import React from "react";
import { useTranslation } from "react-i18next";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { Stack, Typography } from "@mui/material";
import Selection from "components/Input/Selection/Selection";
import RadioInput from "components/Input/RadioInput/RadioInput";
import CheckboxInput from "components/Input/CheckboxInput/CheckboxInput";
import AmexOwnershipTypeIndicatorOptions from "utils/AmexOptions/AmexOwnershipTypeIndicatorOptions";
const config = require(
  `../../../../../../config/${process.env.REACT_APP_ENV}/env.json`,
);
import SignificantOwnersSection from "./SignificantOwnersSection";
import AuthorizedSignerSection from "./AuthorizedSignerSection";
import AmexMerchantDetailSection from "./AmexMerchantDetailSection";

import { Controller } from "react-hook-form";
import { CustomComponentCommonProps, rules } from "../../utils";

const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";

const AmexBbmslForm = ({ control, mccOptions, updateGsiState }) => {
  const { t } = useTranslation();
  return (
    <Stack gap={3}>
      <Stack
        sx={{
          border: "1px solid rgb(222, 222, 222)",
          borderRadius: "8px",
          padding: "1rem",
        }}
      >
        <Typography variant="h3" sx={{ marginBottom: "1rem" }}>
          {t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.subtitle.basicInfo`)}
        </Typography>
        <Stack sx={{ gap: 3 }}>
          <Stack
            direction="row"
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={3}
          >
            <Controller
              name="amexBbmslMid"
              control={control}
              defaultValue={config.AMEX_BBMSL_MID}
              render={({ field: { onChange } }) => (
                <OutlinedText
                  {...CustomComponentCommonProps(
                    t(
                      `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.amexBbmslMid`,
                    ),
                    config.AMEX_BBMSL_MID,
                    "OutlinedText",
                    true,
                    false,
                  )}
                  noErrorMessage
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="midActive"
              control={control}
              render={({ field: { value } }) => (
                <CheckboxInput
                  disabled
                  label={t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.activate`,
                  )}
                  checked={value}
                />
              )}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={3}
          >
            <Controller
              name="amexBbmslMid2"
              control={control}
              defaultValue={config.AMEX_BBMSL_MID_2}
              render={({ field: { onChange } }) => (
                <OutlinedText
                  {...CustomComponentCommonProps(
                    t(
                      `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.amexBbmslMid2`,
                    ),
                    config.AMEX_BBMSL_MID_2,
                    "OutlinedText",
                    true,
                    false,
                  )}
                  noErrorMessage
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="mid2Active"
              control={control}
              render={({ field: { value } }) => (
                <CheckboxInput
                  disabled
                  label={t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.activate`,
                  )}
                  checked={value}
                />
              )}
            />
          </Stack>

          <Controller
            name="language"
            control={control}
            defaultValue=""
            rules={rules({ required: true })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                options={[
                  {
                    key: "en",
                    title: "EN : EN - ENGLISH",
                    value: "EN",
                  },
                  {
                    key: "zh",
                    title: "ZH : ZH - CHINESE",
                    value: "ZH",
                  },
                  {
                    key: "z1",
                    title: "Z1 : Z1 - CHINESE - CANTONESE",
                    value: "Z1",
                  },
                  {
                    key: "z2",
                    title: "Z2 : Z2 - CHINESE - MANDARIN",
                    value: "Z2",
                  },
                ]}
                {...CustomComponentCommonProps(
                  t(`${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.language`),
                  value,
                  "Selection",
                  true,
                  true,
                )}
                sxParams={{
                  selectionError: !!error,
                }}
              />
            )}
          />
          <Controller
            name="marketingIndicator"
            control={control}
            rules={rules({ required: true })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <RadioInput
                fontWeight=""
                label={t(
                  `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.marketingIndicator`,
                )}
                value={value}
                onChange={onChange}
                options={[
                  {
                    label:
                      "Y = All Marketing (Seller has not opted out of receiving marketing)",
                    value: "true",
                  },
                  {
                    label:
                      "N = No Marketing (Seller has opted out of receiving marketing)",
                    value: "false",
                  },
                ]}
                mandatory
                sxParams={{ radioInputError: error }}
                errorMessage={error?.message || ""}
              />
            )}
          />
          <Controller
            name="typeIndicator"
            control={control}
            defaultValue=""
            rules={rules({ required: true })}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Selection
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                options={AmexOwnershipTypeIndicatorOptions("all")}
                {...CustomComponentCommonProps(
                  t(
                    `${PAGE_LOCALE_KEY}processorMdr.amexForm.fields.typeIndicator`,
                  ),
                  value,
                  "Selection",
                  true,
                  true,
                )}
                sxParams={{
                  selectionError: !!error,
                }}
              />
            )}
          />
        </Stack>
      </Stack>

      <Stack
        sx={{
          border: "1px solid rgb(222, 222, 222)",
          borderRadius: "8px",
        }}
      >
        <SignificantOwnersSection />
      </Stack>

      <Stack
        sx={{
          border: "1px solid rgb(222, 222, 222)",
          borderRadius: "8px",
        }}
      >
        <AuthorizedSignerSection />
      </Stack>

      <Stack
        sx={{
          border: "1px solid rgb(222, 222, 222)",
          borderRadius: "8px",
        }}
      >
        <AmexMerchantDetailSection mccOptions={mccOptions} updateGsiState={updateGsiState} />
      </Stack>
    </Stack>
  );
};

export default React.memo(AmexBbmslForm);
